import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./NoteEditorPage.css";
import DepartmentMasterModal from "./DepartmentMasterModal";

// Match the ERP production .env setting; never fall back to localhost on a live site.
const API = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:8080" : "")
).replace(/\/+$/, "");

const authHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const TOOL_ITEMS = [
  {
    type: "paragraph",
    icon: "¶",
    label: "Add Para",
  },
  {
    type: "sketch",
    icon: "✏",
    label: "Add Sketch",
  },
  {
    type: "points",
    icon: "☷",
    label: "Add Points",
  },
  {
    type: "image",
    icon: "▧",
    label: "Add Image",
  },
  {
    type: "document",
    icon: "📄",
    label: "Add Docs",
  },
  {
    type: "link",
    icon: "🔗",
    label: "Add Link",
  },

  {
    type: "checklist",
    icon: "☑",
    label: "Add Checklist",
  },
  {
    type: "template",
    icon: "▦",
    label: "Add Template",
  },
];

const cardLabel = (type) =>
  ({
    paragraph: "Paragraph",
    sketch: "Sketch",
    points: "Points",
    image: "Image",
    document: "Document",
    link: "Link",
    subheading: "Subheading",
    checklist: "Checklist",
    template: "Template",
  })[type] || "Component";

// Persistent MongoDB ID for saved cards; client ID for new cards.
const cardKey = (block) => String(block._clientId || block._id || "");
const mongoId = (value) => value?._id || value || "";
const sameUserId = (a, b) =>
  Boolean(mongoId(a) && mongoId(b)) &&
  String(mongoId(a)) === String(mongoId(b));

// Status is derived from saved server sharing records, never from a clicked button.
const isSharedWith = (shares, userId) =>
  (shares || []).some((entry) => sameUserId(entry.user, userId));

const safeExternalUrl = (value) => {
  try {
    const url = new URL(String(value || "").trim());
    if (!["http:", "https:"].includes(url.protocol) || !url.hostname) return "";
    return url.href;
  } catch {
    return "";
  }
};

const createBlock = (type) => ({
  _clientId: Date.now().toString() + Math.random().toString(36),

  type,

  title: "",

  // Optional subtitle belonging to this card.
  subhead: "",
  description: "",

  content: "",

  items:
    type === "points" || type === "checklist"
      ? [
          {
            text: "",
            checked: false,
          },
        ]
      : [],

  url: "",
  fileName: "",
  fileKey: "",
  fileUrl: "",
  sketchData: "",
  project: null,
});

const NoteEditorPage = () => {
  const { noteId } = useParams();
  const navigate = useNavigate();

  const [note, setNote] = useState(null);

  const [projects, setProjects] = useState([]);

  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [saving, setSaving] = useState(false);

  const [showShare, setShowShare] = useState(false);

  const [email, setEmail] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [showDepartment, setShowDepartment] = useState(false);
  const [showDepartmentMaster, setShowDepartmentMaster] = useState(false);
  const [canManageDepartments, setCanManageDepartments] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [cardShareIndex, setCardShareIndex] = useState(null);
  const [cardShareBusy, setCardShareBusy] = useState(false);
  const [cardShareFeedback, setCardShareFeedback] = useState("");
  const [noteShareFeedback, setNoteShareFeedback] = useState("");
  const [noteShareBusy, setNoteShareBusy] = useState(false);
  const [highlightedCard, setHighlightedCard] = useState("");
  const pendingScrollRef = useRef("");
  const noteRef = useRef(null);
  const pendingSaveRef = useRef(null);

  useEffect(() => {
    noteRef.current = note;
  }, [note]);

  // Scroll to the newly-added card, highlight it and focus its title.
  useEffect(() => {
    const key = pendingScrollRef.current;
    if (!key) return undefined;
    const element = document.getElementById(`note-card-${key}`);
    if (!element) return undefined;
    pendingScrollRef.current = "";
    setHighlightedCard(key);
    const frame = window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      const nameButton = element.querySelector(".note-card-name-button");
      if (nameButton) nameButton.focus({ preventScroll: true });
    });
    const timer = window.setTimeout(() => setHighlightedCard(""), 2200);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [note?.blocks?.length]);

  useEffect(() => {
    loadNote();
  }, [noteId]);

  const loadNote = async () => {
    try {
      setLoading(true);
      setLoadError("");

      if (!API) {
        throw new Error(
          "Production API URL is missing. Configure REACT_APP_API_BASE_URL and rebuild the frontend.",
        );
      }

      // Keep the token source consistent with your existing ERP Notes page.
      // Do not invent another storage key or send credentials in the URL.
      if (!localStorage.getItem("token")?.trim()) {
        throw new Error("ERP login token is missing. Please log in again.");
      }

      const [noteResponse, masters, departmentResponse] = await Promise.all([
        axios.get(`${API}/api/notes/${noteId}`, {
          headers: authHeaders(),
        }),
        axios.get(`${API}/api/notes/masters`, {
          headers: authHeaders(),
        }),
        // Independent Notes Department Master, never ERP Role.
        axios.get(`${API}/api/notes/departments`, {
          headers: authHeaders(),
        }),
      ]);

      const loadedNote =
        noteResponse.data?.note || noteResponse.data?.data || noteResponse.data;
      setNote(loadedNote);
      setSelectedDepartment(
        loadedNote?.department?._id || loadedNote?.department || "",
      );
      setDepartments(
        Array.isArray(departmentResponse.data?.departments)
          ? departmentResponse.data.departments
          : [],
      );
      setCanManageDepartments(
        departmentResponse.data?.canManageDepartments === true,
      );

      setProjects(masters.data?.projects || []);

      setUsers(masters.data?.users || []);
    } catch (error) {
      // Diagnostic metadata only. Never log or share the JWT/token value.
      const status = error.response?.status;
      const message =
        error.response?.data?.message ||
        error.message ||
        "Unable to load note.";
      console.error("NOTE EDITOR LOAD FAILED:", {
        url: error.config?.url || `${API}/api/notes/${noteId}`,
        status,
        message,
        tokenPresent: Boolean(localStorage.getItem("token")?.trim()),
      });

      setLoadError(
        status === 401
          ? `Authentication failed (401): ${message}. Check that the Notes request uses the same API host and login token as the working Notes list.`
          : status === 403
            ? `Access denied (403): ${message}`
            : message,
      );
      // Do not redirect: keep the actual error visible for troubleshooting.
    } finally {
      setLoading(false);
    }
  };

  const addBlock = (type) => {
    const newCard = createBlock(type);
    pendingScrollRef.current = cardKey(newCard);
    setNote((previous) => ({
      ...previous,
      blocks: [...(previous.blocks || []), newCard],
    }));
  };

  const updateBlock = (index, field, value) => {
    setNote((previous) => {
      const blocks = [...(previous.blocks || [])];

      if (!blocks[index]) return previous;
      const patch = typeof field === "object" ? field : { [field]: value };
      blocks[index] = {
        ...blocks[index],
        ...patch,
      };

      return {
        ...previous,
        blocks,
      };
    });
  };

  const deleteBlock = (index) => {
    setNote((previous) => ({
      ...previous,

      blocks: previous.blocks.filter((_, blockIndex) => blockIndex !== index),
    }));
  };

  const moveBlock = (index, direction) => {
    setNote((previous) => {
      const blocks = [...previous.blocks];

      const newIndex = index + direction;

      if (newIndex < 0 || newIndex >= blocks.length) {
        return previous;
      }

      [blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]];

      return {
        ...previous,
        blocks,
      };
    });
  };

  const saveNote = async ({ silent = false } = {}) => {
    // Prevent simultaneous saves from creating duplicate card IDs.
    if (pendingSaveRef.current) return pendingSaveRef.current;

    const performSave = async () => {
      setSaving(true);
      try {
        const current = noteRef.current || note;
        if (!current) throw new Error("Note has not loaded.");

        const payload = {
          name: current.name,
          project: mongoId(current.project) || null,
          blocks: (current.blocks || []).map((block, index) => ({
            ...(block._id ? { _id: mongoId(block._id) } : {}),
            type: block.type,
            order: index,
            title: block.title || "",
            subhead: block.subhead || "",
            description: block.description || "",
            project: mongoId(block.project) || null,
            content: block.content || "",
            items: block.items || [],
            url: block.url || "",
            fileName: block.fileName || "",
            fileKey: block.fileKey || "",
            fileUrl: block.fileUrl || "",
            sketchData: block.sketchData || "",
            // Never send sharedWith: only backend may change card permissions.
          })),
        };

        const response = await axios.put(
          `${API}/api/notes/${noteId}`,
          payload,
          {
            headers: authHeaders(),
          },
        );
        const updated = response.data?.note || response.data?.data;
        if (!updated?.blocks) {
          throw new Error(
            "Save response did not return the saved cards. Check noteController.js.",
          );
        }
        noteRef.current = updated;
        setNote(updated);
        if (!silent) alert("Note saved.");
        return updated;
      } catch (error) {
        console.error("SAVE NOTE ERROR:", error);
        alert(
          error.response?.data?.message ||
            error.message ||
            "Unable to save note.",
        );
        return null;
      } finally {
        setSaving(false);
      }
    };

    const operation = performSave();
    pendingSaveRef.current = operation;
    try {
      return await operation;
    } finally {
      pendingSaveRef.current = null;
    }
  };

  // A persisted card already has an ID. Never PUT the entire note just to share it.
  // The backend checks card-level permissions before recording the share.
  const getCardForAction = async () => {
    const current = noteRef.current || note;
    let selected = current?.blocks?.[cardShareIndex];
    if (!selected) throw new Error("Selected card is no longer available.");

    if (!selected._id) {
      // Only a new card requires a whole-note save to receive its MongoDB ID.
      // A card-only viewer cannot create/save cards through the whole-note endpoint.
      const saved = await saveNote({ silent: true });
      if (!saved) return null;
      selected = saved.blocks?.[cardShareIndex];
    }
    if (!selected?._id)
      throw new Error("Card ID is missing. Reload the note and retry.");
    return selected;
  };

  const shareSelectedCard = async (targetUserId) => {
    if (cardShareIndex === null || cardShareBusy) return;
    setCardShareBusy(true);
    setCardShareFeedback("");
    try {
      const card = await getCardForAction();
      if (!card) return;
      const response = await axios.post(
        `${API}/api/notes/${noteId}/cards/${mongoId(card._id)}/share`,
        { userId: targetUserId, permission: "view" },
        { headers: authHeaders() },
      );
      if (response.data?.success !== true) {
        throw new Error("Server did not confirm the card share.");
      }

      const savedShares = response.data.sharedWith;
      const confirmedUserId =
        mongoId(response.data.sharedUser?._id) || targetUserId;
      // Update only this card's permissions: never replace unsaved note content.
      setNote((previous) => ({
        ...previous,
        blocks: (previous.blocks || []).map((item) => {
          if (String(mongoId(item._id)) !== String(mongoId(card._id)))
            return item;
          const shares = Array.isArray(savedShares)
            ? savedShares
            : [
                ...(item.sharedWith || []).filter(
                  (entry) => !sameUserId(entry.user, confirmedUserId),
                ),
                {
                  user: confirmedUserId,
                  permission: "view",
                  sharedAt: new Date().toISOString(),
                },
              ];
          return { ...item, sharedWith: shares };
        }),
      }));
      setCardShareFeedback(
        response.data.message || "✓ Card shared successfully.",
      );
    } catch (error) {
      console.error("CARD SHARE ERROR:", error);
      alert(
        error.response?.data?.message ||
          error.message ||
          "Unable to share card.",
      );
    } finally {
      setCardShareBusy(false);
    }
  };

  // Send ONLY the selected saved card via the dedicated backend endpoint.
  // A logged-in ERP email address does not itself authorize sending from that mailbox.
  const emailSelectedCard = async (recipient) => {
    if (cardShareIndex === null || cardShareBusy) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(recipient || "").trim())) {
      alert("Please enter a valid email address.");
      return;
    }
    setCardShareBusy(true);
    try {
      const card = await getCardForAction();
      if (!card) return;
      const response = await axios.post(
        `${API}/api/notes/${noteId}/cards/${mongoId(card._id)}/email`,
        { email: String(recipient).trim() },
        { headers: authHeaders() },
      );
      if (!response.data?.success) {
        throw new Error("Mail server did not accept the email.");
      }
      setCardShareIndex(null);
      alert(
        response.data.message ||
          "Mail accepted by sending provider. Check delivery separately.",
      );
    } catch (error) {
      console.error("CARD EMAIL ERROR:", error);
      alert(
        error.response?.data?.message ||
          error.message ||
          "Unable to email this card.",
      );
    } finally {
      setCardShareBusy(false);
    }
  };

  const shareUser = async (userId, permission) => {
    if (noteShareBusy) return;
    setNoteShareBusy(true);
    setNoteShareFeedback("");
    try {
      const response = await axios.post(
        `${API}/api/notes/${noteId}/share`,
        { userId, permission },
        { headers: authHeaders() },
      );
      if (response.data?.success !== true) {
        throw new Error("Server did not confirm the note share.");
      }

      // Keep local unsaved edits; refresh only the server-confirmed share list.
      const serverShares = response.data?.note?.sharedWith;
      setNote((previous) => ({
        ...previous,
        sharedWith: Array.isArray(serverShares)
          ? serverShares
          : [
              ...(previous.sharedWith || []).filter(
                (entry) => !sameUserId(entry.user, userId),
              ),
              { user: userId, permission, sharedAt: new Date().toISOString() },
            ],
      }));
      setNoteShareFeedback(
        response.data.message || "✓ Note shared successfully.",
      );
    } catch (error) {
      console.error("NOTE SHARE ERROR:", error);
      alert(
        error.response?.data?.message ||
          error.message ||
          "Unable to share note.",
      );
    } finally {
      setNoteShareBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!email.trim()) return;

    try {
      await axios.post(
        `${API}/api/notes/${noteId}/email`,
        {
          emails: [email.trim()],
        },
        {
          headers: authHeaders(),
        },
      );

      setEmail("");

      alert("Email processed.");
    } catch (error) {
      console.error(error);

      alert("Unable to email note.");
    }
  };

  // Refresh only the independent master after an Add/Edit/Deactivate action.
  // Do not reload the note here: it may contain unsaved editor changes.
  const refreshNoteDepartments = async () => {
    const response = await axios.get(`${API}/api/notes/departments`, {
      headers: authHeaders(),
    });
    setDepartments(
      Array.isArray(response.data?.departments)
        ? response.data.departments
        : [],
    );
    setCanManageDepartments(response.data?.canManageDepartments === true);
  };

  const saveDepartment = async () => {
    if (!selectedDepartment) return alert("Please select a department.");
    const department = departments.find(
      (item) => String(item._id) === String(selectedDepartment),
    );
    if (!department || department.isActive === false) {
      return alert("Please select an active Notes department.");
    }
    try {
      setActionBusy(true);
      const response = await axios.patch(
        `${API}/api/notes/${noteId}/department`,
        {
          department: selectedDepartment,
          departmentName: department.name,
        },
        { headers: authHeaders() },
      );
      if (response.data?.note) setNote(response.data.note);
      else
        setNote((previous) => ({
          ...previous,
          department: selectedDepartment,
          departmentName: department.name,
        }));
      setShowDepartment(false);
      alert("Department updated.");
    } catch (error) {
      console.error("DEPARTMENT UPDATE ERROR:", error);
      alert(
        error.response?.data?.message ||
          "Unable to update department. Check backend PATCH route.",
      );
    } finally {
      setActionBusy(false);
    }
  };

  const sendEmailFromIcon = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return alert("Enter a valid email address.");
    }
    try {
      setActionBusy(true);
      await axios.post(
        `${API}/api/notes/${noteId}/email`,
        { emails: [email.trim()] },
        { headers: authHeaders() },
      );
      setEmail("");
      setShowEmail(false);
      alert(
        "Email request processed. Actual delivery depends on your email backend.",
      );
    } catch (error) {
      console.error("NOTE EMAIL ERROR:", error);
      alert(error.response?.data?.message || "Unable to process email.");
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return <div className="note-editor-loading">Loading note...</div>;
  }

  if (loadError) {
    return (
      <div className="note-editor-loading" role="alert" style={{ padding: 24 }}>
        <p>{loadError}</p>
        <button type="button" onClick={loadNote}>
          Retry
        </button>{" "}
        <button type="button" onClick={() => navigate("/tools/notes")}>
          Back to Notes
        </button>
      </div>
    );
  }

  if (!note) return null;

  return (
    <div className="note-tool-page">
      {/* HEADER */}

      <header className="note-tool-header">
        <button
          className="note-back-button"
          onClick={() => navigate("/tools/notes")}
        >
          ←
        </button>

        <div className="note-header-main">
          <input
            className="note-heading-input"
            value={note.name || ""}
            onChange={(event) =>
              setNote({
                ...note,
                name: event.target.value,
              })
            }
          />

          <div className="note-header-meta">
            <span>🏢 {note.department?.name || note.departmentName}</span>

            <span>🗂 {note.category}</span>

            {note.project?.name && <span>📁 {note.project.name}</span>}
          </div>
        </div>

        <div className="note-header-actions">
          <button
            type="button"
            className="note-header-icon-button"
            title="Change Department"
            aria-label="Change Department"
            onClick={() => setShowDepartment(true)}
          >
            <span aria-hidden="true">🏢</span>
            <small>Department</small>
          </button>
          <button
            type="button"
            className="note-header-icon-button"
            title="Share Note"
            aria-label="Share Note"
            onClick={() => {
              setNoteShareFeedback("");
              setShowShare(true);
            }}
          >
            <span aria-hidden="true">↗</span>
            <small>Share</small>
          </button>
          <button
            type="button"
            className="note-header-icon-button"
            title="Email Note"
            aria-label="Email Note"
            onClick={() => setShowEmail(true)}
          >
            <span aria-hidden="true">✉</span>
            <small>Email</small>
          </button>
        </div>

        <button
          className="note-save-button"
          onClick={() => saveNote()}
          disabled={saving}
        >
          {saving ? "Saving..." : "✓ Save"}
        </button>
      </header>

      <div className="note-workspace">
        {/* NOTE CANVAS */}

        <main className="note-canvas">
          {(!note.blocks || note.blocks.length === 0) && (
            <div className="note-start-empty">
              <div>📝</div>

              <h2>Start building your note</h2>

              <p>Select a component from the right side.</p>
            </div>
          )}

          {(note.blocks || []).map((block, index) => (
            <BlockCard
              key={block._id || block._clientId || index}
              block={block}
              index={index}
              updateBlock={updateBlock}
              deleteBlock={deleteBlock}
              moveBlock={moveBlock}
              projects={projects}
              onShare={() => {
                setCardShareFeedback("");
                setCardShareIndex(index);
              }}
              highlighted={highlightedCard === cardKey(block)}
            />
          ))}

          {/* PROJECT */}

          <div className="note-project-selector">
            <div>
              <strong>Whole Note Project (optional)</strong>

              <small>
                Each card also has its own separate project selector below.
              </small>
            </div>

            <select
              value={note.project?._id || note.project || ""}
              onChange={(event) =>
                setNote({
                  ...note,
                  project: event.target.value,
                })
              }
            >
              <option value="">No Project</option>

              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="note-bottom-save">
            <button
              className="note-save-button"
              onClick={() => saveNote()}
              disabled={saving}
            >
              {saving ? "Saving..." : "✓ Save Note"}
            </button>
          </div>
        </main>

        {/* RIGHT TOOLS */}

        <aside className="note-tools-panel">
          <div className="tools-panel-heading">
            <strong>Add Component</strong>

            <span>Build your note</span>
          </div>

          <div className="note-tool-grid">
            {TOOL_ITEMS.map((tool) => (
              <button key={tool.type} onClick={() => addBlock(tool.type)}>
                <span>{tool.icon}</span>

                {tool.label}
              </button>
            ))}
          </div>
        </aside>
      </div>

      {showDepartment && (
        <div
          className="note-modal-overlay"
          onMouseDown={() => setShowDepartment(false)}
        >
          <div
            className="share-note-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="note-modal-header">
              <div>
                <h2>Change Department</h2>
                <p>Select the department for this note.</p>
              </div>
              <button type="button" onClick={() => setShowDepartment(false)}>
                ×
              </button>
            </div>
            <div className="share-modal-content note-icon-modal-form">
              <label htmlFor="note-department-choice">Department</label>
              <select
                id="note-department-choice"
                value={selectedDepartment}
                onChange={(event) => setSelectedDepartment(event.target.value)}
              >
                <option value="">Select Department</option>
                {departments
                  .filter((department) => department.isActive !== false)
                  .map((department) => (
                    <option key={department._id} value={department._id}>
                      {department.name}
                    </option>
                  ))}
                {selectedDepartment &&
                  !departments.some(
                    (department) =>
                      String(department._id) === String(selectedDepartment) &&
                      department.isActive !== false,
                  ) && (
                    <option value={selectedDepartment} disabled>
                      {note.departmentName || "Previous Department"} (inactive /
                      unavailable)
                    </option>
                  )}
              </select>
              {canManageDepartments && (
                <button
                  type="button"
                  onClick={() => setShowDepartmentMaster(true)}
                  disabled={actionBusy}
                >
                  + Manage Department Master
                </button>
              )}
              <button
                type="button"
                onClick={saveDepartment}
                disabled={actionBusy || !selectedDepartment}
              >
                {actionBusy ? "Saving..." : "Save Department"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDepartmentMaster && canManageDepartments && (
        <DepartmentMasterModal
          onClose={() => setShowDepartmentMaster(false)}
          onChanged={refreshNoteDepartments}
        />
      )}

      {showEmail && (
        <div
          className="note-modal-overlay"
          onMouseDown={() => setShowEmail(false)}
        >
          <div
            className="share-note-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="note-modal-header">
              <div>
                <h2>Email Note</h2>
                <p>Enter recipient email address.</p>
              </div>
              <button type="button" onClick={() => setShowEmail(false)}>
                ×
              </button>
            </div>
            <div className="share-modal-content note-icon-modal-form">
              <label htmlFor="note-email-recipient">Recipient Email</label>
              <input
                id="note-email-recipient"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") sendEmailFromIcon();
                }}
              />
              <button
                type="button"
                onClick={sendEmailFromIcon}
                disabled={actionBusy}
              >
                {actionBusy ? "Processing..." : "Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShare && (
        <ShareModal
          users={users}
          email={email}
          setEmail={setEmail}
          shareUser={shareUser}
          sendEmail={sendEmail}
          sharedWith={note.sharedWith || []}
          ownerId={mongoId(note.createdBy)}
          busy={noteShareBusy}
          feedback={noteShareFeedback}
          close={() => !noteShareBusy && setShowShare(false)}
        />
      )}

      {cardShareIndex !== null && (
        <CardShareModal
          users={users}
          card={note.blocks?.[cardShareIndex]}
          noteShares={note.sharedWith || []}
          ownerId={mongoId(note.createdBy)}
          feedback={cardShareFeedback}
          busy={cardShareBusy || saving}
          shareUser={shareSelectedCard}
          emailCard={emailSelectedCard}
          close={() => !cardShareBusy && setCardShareIndex(null)}
        />
      )}
    </div>
  );
};

const BlockCard = ({
  block,
  index,
  updateBlock,
  deleteBlock,
  moveBlock,
  projects,
  onShare,
  highlighted,
}) => {
  const label = cardLabel(block.type);
  const key = cardKey(block);
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(block.title || "");
  const titleInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const subheadInputRef = useRef(null);
  const [subheadOpen, setSubheadOpen] = useState(Boolean(block.subhead));
  const [editingSubhead, setEditingSubhead] = useState(false);
  const [draftSubhead, setDraftSubhead] = useState(block.subhead || "");
  const cancelSubheadRef = useRef(false);
  const cancelEditRef = useRef(false);

  useEffect(() => {
    if (block.subhead && !subheadOpen) setSubheadOpen(true);
  }, [block.subhead, subheadOpen]);

  useEffect(() => {
    if (!editingSubhead) setDraftSubhead(block.subhead || "");
  }, [block.subhead, editingSubhead]);

  useEffect(() => {
    if (editingSubhead) {
      subheadInputRef.current?.focus({ preventScroll: true });
      subheadInputRef.current?.select();
    }
  }, [editingSubhead]);

  const openSubhead = () => {
    setDraftSubhead(block.subhead || "");
    setSubheadOpen(true);
    setEditingSubhead(true);
  };

  const finishSubhead = () => {
    if (cancelSubheadRef.current) {
      cancelSubheadRef.current = false;
      setEditingSubhead(false);
      setDraftSubhead(block.subhead || "");
      if (!block.subhead?.trim()) setSubheadOpen(false);
      return;
    }

    const next = draftSubhead.trim();
    if (next !== (block.subhead || "")) updateBlock(index, "subhead", next);
    setEditingSubhead(false);
    if (!next) setSubheadOpen(false);
  };

  const removeSubhead = () => {
    cancelSubheadRef.current = false;
    updateBlock(index, "subhead", "");
    setDraftSubhead("");
    setEditingSubhead(false);
    setSubheadOpen(false);
  };

  useEffect(() => {
    if (!editingTitle) setDraftTitle(block.title || "");
  }, [block.title, editingTitle]);

  // An added card opens its inline rename box automatically.
  useEffect(() => {
    if (highlighted && !block._id) setEditingTitle(true);
  }, [highlighted, block._id]);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus({ preventScroll: true });
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  const startRename = () => {
    setDraftTitle(block.title || "");
    setEditingTitle(true);
  };

  const finishRename = () => {
    if (cancelEditRef.current) {
      cancelEditRef.current = false;
      setEditingTitle(false);
      setDraftTitle(block.title || "");
      return;
    }
    const renamed = draftTitle.trim();
    if (renamed !== (block.title || "")) {
      updateBlock(index, "title", renamed);
    }
    setEditingTitle(false);
  };

  return (
    <section
      id={`note-card-${key}`}
      className={`note-block-card ${highlighted ? "note-card-new-highlight" : ""}`}
    >
      <div className="note-block-header">
        <div className="note-card-heading">
          <span className="drag-handle" aria-hidden="true">
            ⋮⋮
          </span>
          <div className="note-card-title-area">
            {editingTitle ? (
              <input
                ref={titleInputRef}
                className="note-card-title-input"
                aria-label={`Rename ${label} card`}
                placeholder={`Name this ${label.toLowerCase()} card...`}
                value={draftTitle}
                onChange={(event) => setDraftTitle(event.target.value)}
                onBlur={finishRename}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                  if (event.key === "Escape") {
                    cancelEditRef.current = true;
                    event.currentTarget.blur();
                  }
                }}
              />
            ) : (
              <button
                type="button"
                className="note-card-name-button"
                title="Double-click to rename this card"
                aria-label={`${block.title || label}. Double-click to rename.`}
                onDoubleClick={startRename}
                onKeyDown={(event) => {
                  if (event.key === "F2" || event.key === "Enter") {
                    event.preventDefault();
                    startRename();
                  }
                }}
              >
                {block.title?.trim() || label}
              </button>
            )}
            {!editingTitle && (
              <button
                type="button"
                className="note-card-rename-button"
                title="Rename card"
                aria-label={`Rename ${block.title || label}`}
                onClick={startRename}
              >
                ✎
              </button>
            )}
          </div>
        </div>
        <div className="note-card-actions">
          <button
            type="button"
            className="note-card-share-button"
            onClick={onShare}
            title="Share this card only"
            aria-label={`Share ${block.title || label} only`}
          >
            ↗ <span>Share card</span>
          </button>
          <button
            type="button"
            onClick={() => moveBlock(index, -1)}
            title="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => moveBlock(index, 1)}
            title="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            className="block-delete"
            onClick={() => {
              if (window.confirm(`Delete ${block.title || label} card?`))
                deleteBlock(index);
            }}
            title="Delete card"
          >
            ×
          </button>
        </div>
      </div>

      <div className="note-block-body">
        {block.type === "paragraph" && (
          <RichParagraph
            value={block.content || ""}
            onChange={(value) => updateBlock(index, "content", value)}
          />
        )}

        {block.type === "subheading" && (
          <input
            className="block-subheading"
            placeholder="Subheading..."
            value={block.content || ""}
            onChange={(event) =>
              updateBlock(index, "content", event.target.value)
            }
          />
        )}

        {block.type === "link" && (
          <div className="note-link-row">
            <input
              type="text"
              className="note-link-name-input"
              aria-label="Link name"
              placeholder="Link name (e.g. SDC)"
              value={block.content || ""}
              onChange={(event) =>
                updateBlock(index, "content", event.target.value)
              }
            />
            <div className="note-link-url-wrap">
              <input
                type="url"
                aria-label="Link URL"
                placeholder="https://..."
                value={block.url || ""}
                onChange={(event) =>
                  updateBlock(index, "url", event.target.value)
                }
              />
              {safeExternalUrl(block.url) ? (
                <a
                  className="note-link-open-icon"
                  href={safeExternalUrl(block.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open link in a new tab"
                  aria-label={`Open ${block.content || "link"} in a new tab`}
                >
                  ↗
                </a>
              ) : (
                <button
                  type="button"
                  className="note-link-open-icon"
                  disabled
                  title="Enter a valid http:// or https:// link first"
                  aria-label="Open link (enter a valid URL first)"
                >
                  ↗
                </button>
              )}
            </div>
          </div>
        )}

        {(block.type === "points" || block.type === "checklist") && (
          <ListBlock block={block} index={index} updateBlock={updateBlock} />
        )}

        {block.type === "sketch" && (
          <SketchBlock
            value={block.sketchData}
            onChange={(value) => updateBlock(index, "sketchData", value)}
          />
        )}

        {block.type === "image" && (
          <FileBlock
            accept="image/*"
            block={block}
            index={index}
            updateBlock={updateBlock}
            image
          />
        )}

        {block.type === "document" && (
          <FileBlock
            block={block}
            index={index}
            updateBlock={updateBlock}
            uploadInputRef={documentInputRef}
          />
        )}

        {block.type === "template" && (
          <textarea
            placeholder="Enter reusable template content..."
            value={block.content || ""}
            onChange={(event) =>
              updateBlock(index, "content", event.target.value)
            }
          />
        )}
      </div>

      {/* Project belongs to this specific card, not the entire note. */}
      <div className="note-card-project-footer">
        <label htmlFor={`note-card-project-${key}`}>
          📁 Project for this card
        </label>
        <select
          id={`note-card-project-${key}`}
          value={mongoId(block.project) || ""}
          onChange={(event) =>
            updateBlock(index, "project", event.target.value || null)
          }
        >
          <option value="">No Project</option>
          {projects.map((project) => (
            <option key={project._id} value={project._id}>
              {project.name}
            </option>
          ))}
        </select>
        {block.type === "document" && (
          <button
            type="button"
            className="note-document-add-footer"
            onClick={() => documentInputRef.current?.click()}
            title="Upload more documents to this card"
          >
            + Add Document
          </button>
        )}
      </div>
    </section>
  );
};

const ListBlock = ({ block, index, updateBlock }) => {
  const items = block.items || [];

  const updateItem = (itemIndex, field, value) => {
    const next = items.map((item, i) =>
      i === itemIndex
        ? {
            ...item,
            [field]: value,
          }
        : item,
    );

    updateBlock(index, "items", next);
  };

  const addItem = () => {
    updateBlock(index, "items", [
      ...items,
      {
        text: "",
        checked: false,
      },
    ]);
  };

  const removeItem = (itemIndex) => {
    updateBlock(
      index,
      "items",
      items.filter((_, i) => i !== itemIndex),
    );
  };

  return (
    <div className="note-list-editor">
      {items.map((item, i) => (
        <div className="note-list-row" key={i}>
          {block.type === "checklist" ? (
            <input
              type="checkbox"
              checked={!!item.checked}
              onChange={(event) =>
                updateItem(i, "checked", event.target.checked)
              }
            />
          ) : (
            <span>•</span>
          )}

          <input
            value={item.text || ""}
            onChange={(event) => updateItem(i, "text", event.target.value)}
            placeholder="Enter item..."
          />

          <button onClick={() => removeItem(i)}>×</button>
        </div>
      ))}

      <button className="add-list-row" onClick={addItem}>
        + Add Item
      </button>
    </div>
  );
};

// Paragraph content is stored as HTML in the existing content field.
// Legacy plain-text paragraphs are converted to safe HTML on first edit.
const escapeParagraph = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\n/g, "<br>");

const normalizeParagraph = (value) => {
  const text = String(value || "");
  return /<\/?(?:p|div|br|b|strong|i|em|u|ul|ol|li|span)\b/i.test(text)
    ? text
    : escapeParagraph(text);
};

const RichParagraph = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const latestHtml = useRef(null);
  const [active, setActive] = useState({});

  useEffect(() => {
    const html = normalizeParagraph(value);
    if (
      editorRef.current &&
      latestHtml.current !== html &&
      editorRef.current.innerHTML !== html
    ) {
      editorRef.current.innerHTML = html;
    }
    latestHtml.current = html;
  }, [value]);

  const sync = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    latestHtml.current = html;
    onChange(html);
    updateToolbar();
  };

  const updateToolbar = () => {
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        unordered: document.queryCommandState("insertUnorderedList"),
        ordered: document.queryCommandState("insertOrderedList"),
      });
    } catch (_) {
      /* browser may not support command state */
    }
  };

  const format = (command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
    sync();
  };

  const tools = [
    ["bold", "B", "Bold (Ctrl+B)"],
    ["italic", "I", "Italic (Ctrl+I)"],
    ["underline", "U", "Underline (Ctrl+U)"],
    ["insertUnorderedList", "• List", "Bullet list"],
    ["insertOrderedList", "1. List", "Numbered list"],
  ];

  return (
    <div className="note-rich-paragraph">
      <div
        className="note-rich-toolbar"
        role="toolbar"
        aria-label="Paragraph formatting"
      >
        {tools.map(([command, label, title]) => (
          <button
            key={command}
            type="button"
            title={title}
            className={
              active[
                command === "insertUnorderedList"
                  ? "unordered"
                  : command === "insertOrderedList"
                    ? "ordered"
                    : command
              ]
                ? "active"
                : ""
            }
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => format(command)}
          >
            {label}
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        className="note-rich-editor"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder="Write paragraph..."
        onInput={sync}
        onKeyUp={updateToolbar}
        onMouseUp={updateToolbar}
        onPaste={(event) => {
          // Paste as plain text to avoid importing unsafe third-party HTML.
          event.preventDefault();
          document.execCommand(
            "insertText",
            false,
            event.clipboardData.getData("text/plain"),
          );
          sync();
        }}
      />
    </div>
  );
};

// Sketch supports blank canvas, images and PDF pages. The annotated result is saved
// in the existing sketchData field; uploaded originals use the existing notes upload API.
const SketchBlock = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const pointerStartRef = useRef(null);
  const pointerMovedRef = useRef(false);
  const pdfRef = useRef(null);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#202333");
  const [size, setSize] = useState(3);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPages, setPdfPages] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 1280 : window.innerWidth,
    height: typeof window === "undefined" ? 800 : window.innerHeight,
  }));

  // Restore an annotated PDF/image at its original aspect ratio; do not
  // squeeze portrait pages into the old fixed 1000x350 bitmap.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;
    const img = new Image();
    img.onload = () => {
      const current = canvasRef.current;
      if (!current) return;
      current.width = img.naturalWidth || img.width;
      current.height = img.naturalHeight || img.height;
      const ctx = current.getContext("2d");
      ctx.clearRect(0, 0, current.width, current.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = value;
  }, []);

  // Expand the SAME canvas, not a separate image: markings made in full-screen
  // and in the small view remain on one canvas and in sketchData.
  useEffect(() => {
    if (!expanded) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleEscape = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };
    const handleResize = () =>
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    window.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleResize);
    };
  }, [expanded]);

  const openFullScreen = () => {
    setZoom(1);
    setViewport({ width: window.innerWidth, height: window.innerHeight });
    setExpanded(true);
  };

  const fullScreenCanvasStyle = () => {
    if (!expanded || !canvasRef.current) return undefined;
    const canvas = canvasRef.current;
    const fit = Math.min(
      Math.max(1, viewport.width - 72) / canvas.width,
      Math.max(1, viewport.height - 235) / canvas.height,
      1,
    );
    return {
      width: `${Math.round(canvas.width * fit * zoom)}px`,
      height: `${Math.round(canvas.height * fit * zoom)}px`,
      maxWidth: "none",
      maxHeight: "none",
    };
  };

  const paintBackground = (source) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scale = Math.min(1, 1800 / source.width, 2400 / source.height);
    canvas.width = Math.max(1, Math.round(source.width * scale));
    canvas.height = Math.max(1, Math.round(source.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    setZoom(1);
    onChange(canvas.toDataURL("image/png"));
  };

  const showPdfPage = async (pdf, number) => {
    if (!pdf) return;
    setBusy(true);
    setError("");
    try {
      const page = await pdf.getPage(number);
      const initial = page.getViewport({ scale: 1 });
      const viewportForPage = page.getViewport({
        scale: Math.min(2.8, 1600 / initial.width, 2400 / initial.height),
      });
      const temp = document.createElement("canvas");
      temp.width = Math.ceil(viewportForPage.width);
      temp.height = Math.ceil(viewportForPage.height);
      await page.render({
        canvasContext: temp.getContext("2d"),
        viewport: viewportForPage,
      }).promise;
      paintBackground(temp);
      setPdfPage(number);
    } catch (e) {
      setError(e.message || "Could not render PDF page.");
    } finally {
      setBusy(false);
    }
  };

  const switchPdfPage = (direction) => {
    const next = pdfPage + direction;
    if (busy || !pdfRef.current || next < 1 || next > pdfPages) return;
    if (
      window.confirm(
        "Switch page? Current page markings will be replaced. Save the note or use a separate Sketch card to keep this page.",
      )
    ) {
      showPdfPage(pdfRef.current, next);
    }
  };

  const uploadBackground = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (
      !window.confirm(
        "Replace current sketch with this document/page? Existing markings will be replaced.",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
        // Keep the existing pdfjs worker installation used by this ERP.
        const pdfjs = await import("pdfjs-dist/build/pdf");
        pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;
        const buffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: new Uint8Array(buffer) })
          .promise;
        pdfRef.current = pdf;
        setPdfFile(file.name);
        setPdfPages(pdf.numPages);
        await showPdfPage(pdf, 1);
      } else if (file.type.startsWith("image/")) {
        pdfRef.current = null;
        setPdfFile(null);
        setPdfPages(0);
        const objectUrl = URL.createObjectURL(file);
        try {
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = objectUrl;
          });
          paintBackground(img);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      } else {
        throw new Error(
          "Sketch supports images and PDF. Convert Word/Excel to PDF first.",
        );
      }
    } catch (e) {
      setError(e.message || "Unable to open document.");
    } finally {
      setBusy(false);
    }
  };

  const position = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const configure = (ctx) => {
    ctx.globalCompositeOperation = "source-over";
    ctx.lineWidth = tool === "eraser" ? size * 5 : size;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = tool === "eraser" ? "#ffffff" : color;
  };

  const start = (event) => {
    if (busy || (event.pointerType === "mouse" && event.button !== 0)) return;
    event.preventDefault();
    drawing.current = true;
    pointerMovedRef.current = false;
    pointerStartRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    const ctx = canvasRef.current.getContext("2d");
    configure(ctx);
    const point = position(event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const draw = (event) => {
    if (!drawing.current) return;
    const initial = pointerStartRef.current;
    if (
      !pointerMovedRef.current &&
      initial &&
      Math.hypot(
        event.clientX - initial.clientX,
        event.clientY - initial.clientY,
      ) < 4
    )
      return;
    pointerMovedRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    configure(ctx);
    const point = position(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stop = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (pointerMovedRef.current) {
      ctx.closePath();
      onChange(canvas.toDataURL("image/png"));
    } else if (!expanded) {
      // A single tap/click enlarges the document instead of leaving a pen dot.
      ctx.closePath();
      openFullScreen();
    } else {
      // Single click in full-screen still allows a small annotation dot.
      const point = position({
        clientX: pointerStartRef.current.clientX,
        clientY: pointerStartRef.current.clientY,
      });
      ctx.lineTo(point.x + 0.01, point.y + 0.01);
      ctx.stroke();
      ctx.closePath();
      onChange(canvas.toDataURL("image/png"));
    }
    pointerStartRef.current = null;
  };

  const clear = () => {
    if (!window.confirm("Clear the entire sketch and document background?"))
      return;
    const canvas = canvasRef.current;
    canvas.width = 1200;
    canvas.height = 480;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    pdfRef.current = null;
    setPdfFile(null);
    setPdfPages(0);
    setPdfPage(1);
    setZoom(1);
    onChange("");
  };

  return (
    <div className="sketch-wrapper">
      <div className="note-sketch-toolbar">
        <label className="note-sketch-upload">
          📎 Upload image / PDF
          <input
            type="file"
            accept="image/*,.pdf,application/pdf"
            onChange={uploadBackground}
            disabled={busy}
            hidden
          />
        </label>
        <button
          type="button"
          className={tool === "pen" ? "active" : ""}
          onClick={() => setTool("pen")}
        >
          ✏ Pen
        </button>
        <button
          type="button"
          className={tool === "eraser" ? "active" : ""}
          onClick={() => setTool("eraser")}
        >
          ⌫ Eraser
        </button>
        <label className="note-sketch-color">
          Color{" "}
          <input
            type="color"
            value={color}
            onChange={(e) => {
              setColor(e.target.value);
              setTool("pen");
            }}
          />
        </label>
        <label className="note-sketch-size">
          Size{" "}
          <input
            type="range"
            min="1"
            max="20"
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
          />{" "}
          {size}
        </label>
        <button
          type="button"
          className="note-sketch-expand-toolbar"
          onClick={openFullScreen}
        >
          ⛶ Full screen
        </button>
        <button type="button" className="note-sketch-clear" onClick={clear}>
          Clear all
        </button>
      </div>

      {pdfFile && (
        <div className="note-sketch-pages">
          📄 {pdfFile} — Page {pdfPage} of {pdfPages}
          <button
            type="button"
            disabled={busy || pdfPage <= 1}
            onClick={() => switchPdfPage(-1)}
          >
            ← Previous
          </button>
          <button
            type="button"
            disabled={busy || pdfPage >= pdfPages}
            onClick={() => switchPdfPage(1)}
          >
            Next →
          </button>
        </div>
      )}
      {busy && <div className="note-sketch-status">Loading document...</div>}
      {error && <div className="note-upload-error">{error}</div>}

      <div
        className={`note-sketch-stage${expanded ? " note-sketch-stage-expanded" : ""}`}
        role={expanded ? "dialog" : undefined}
        aria-modal={expanded ? "true" : undefined}
        aria-label={expanded ? "Expanded sketch document" : undefined}
      >
        {expanded ? (
          <div className="note-sketch-fullscreen-header">
            <strong>
              ⛶ Sketch / document preview{" "}
              {pdfFile ? `— ${pdfFile} (page ${pdfPage}/${pdfPages})` : ""}
            </strong>
            <div className="note-sketch-fullscreen-controls">
              {pdfFile && (
                <>
                  <button
                    type="button"
                    disabled={busy || pdfPage <= 1}
                    onClick={() => switchPdfPage(-1)}
                  >
                    ← Page
                  </button>
                  <button
                    type="button"
                    disabled={busy || pdfPage >= pdfPages}
                    onClick={() => switchPdfPage(1)}
                  >
                    Page →
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={zoom <= 1}
                onClick={() => setZoom((z) => Math.max(1, z - 0.5))}
                aria-label="Zoom out"
              >
                −
              </button>
              <span aria-live="polite">{Math.round(zoom * 100)}%</span>
              <button
                type="button"
                disabled={zoom >= 4}
                onClick={() => setZoom((z) => Math.min(4, z + 0.5))}
                aria-label="Zoom in"
              >
                +
              </button>
              <button type="button" onClick={() => setZoom(1)}>
                Fit
              </button>
              <button
                type="button"
                className="note-sketch-fullscreen-close"
                onClick={() => setExpanded(false)}
              >
                ✕ Close
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="note-sketch-open-overlay"
            onClick={openFullScreen}
            title="Open document in full screen"
          >
            ⛶ Click to enlarge
          </button>
        )}
        {expanded && (
          <div
            className="note-sketch-fullscreen-tools"
            role="toolbar"
            aria-label="Full screen sketch drawing tools"
          >
            <label
              className={`note-sketch-upload note-sketch-full-upload${busy ? " disabled" : ""}`}
            >
              📎 Upload image / PDF
              <input
                type="file"
                accept="image/*,.pdf,application/pdf"
                onChange={uploadBackground}
                disabled={busy}
                hidden
              />
            </label>
            <button
              type="button"
              className={tool === "pen" ? "active" : ""}
              onClick={() => setTool("pen")}
              aria-pressed={tool === "pen"}
              disabled={busy}
            >
              ✏ Pen
            </button>
            <button
              type="button"
              className={tool === "eraser" ? "active" : ""}
              onClick={() => setTool("eraser")}
              aria-pressed={tool === "eraser"}
              disabled={busy}
            >
              ⌫ Eraser
            </button>
            <label className="note-sketch-full-color">
              Color
              <input
                type="color"
                value={color}
                onChange={(event) => {
                  setColor(event.target.value);
                  setTool("pen");
                }}
                disabled={busy}
                aria-label="Drawing pen color"
              />
            </label>
            <label className="note-sketch-full-size">
              Size
              <input
                type="range"
                min="1"
                max="20"
                value={size}
                onChange={(event) => setSize(Number(event.target.value))}
                disabled={busy}
                aria-label="Pen or eraser size"
              />
              <output>{size}</output>
            </label>
            <button
              type="button"
              className="note-sketch-full-clear"
              onClick={clear}
              disabled={busy}
            >
              Clear all
            </button>
            <span className="note-sketch-full-hint">
              Draw on the document · Save Note after closing
            </span>
          </div>
        )}
        <div className="note-sketch-stage-body">
          <canvas
            ref={canvasRef}
            width="1200"
            height="480"
            style={fullScreenCanvasStyle()}
            className={tool === "eraser" ? "eraser-active" : ""}
            title={
              expanded
                ? "Draw annotations here; use + to zoom"
                : "Click to enlarge; drag to annotate"
            }
            onPointerDown={start}
            onPointerMove={draw}
            onPointerUp={stop}
            onPointerCancel={stop}
            onLostPointerCapture={stop}
          />
        </div>
      </div>
      <small className="note-sketch-hint">
        Click the document to enlarge it. Zoom and annotate in full screen; use
        Close or Esc to return. Save the note to keep markings. For multipage
        PDFs, keep each annotated page in a separate Sketch card.
      </small>
    </div>
  );
};

// Start each document description at the same height as its file box.
// Grow only when its own text needs more space; works on initial load and editing.
const AutoGrowDocumentDescription = ({ id, value, onChange }) => {
  const textareaRef = useRef(null);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return undefined;

    const fitHeight = () => {
      // Measure actual text, not the previous CSS/inline minimum height.
      textarea.style.height = "0px";
      textarea.style.height = `${Math.max(32, textarea.scrollHeight + 2)}px`;
    };

    fitHeight();
    window.addEventListener("resize", fitHeight);
    return () => window.removeEventListener("resize", fitHeight);
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      id={id}
      value={value}
      onChange={(event) => {
        const textarea = event.currentTarget;
        textarea.style.height = "0px";
        textarea.style.height = `${Math.max(32, textarea.scrollHeight + 2)}px`;
        onChange(event.target.value);
      }}
      placeholder="Write description..."
      rows={1}
    />
  );
};

const FileBlock = ({
  accept,
  block,
  index,
  updateBlock,
  image,
  uploadInputRef,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const internalInputRef = useRef(null);
  const inputRef = uploadInputRef || internalInputRef;

  // Every attachment has its OWN description in content's JSON array.
  // For old notes, migrate the former card-level description to file #1 only.
  const files = React.useMemo(() => {
    if (block.content) {
      try {
        const parsed = JSON.parse(block.content);
        if (Array.isArray(parsed)) {
          return parsed.map((file, fileIndex) => ({
            ...file,
            description: image
              ? file.description || ""
              : typeof file.description === "string"
                ? file.description
                : fileIndex === 0
                  ? block.description || ""
                  : "",
          }));
        }
      } catch (_) {
        // Legacy one-file fields handled below.
      }
    }
    if (block.fileName || block.fileUrl || block.fileKey) {
      return [
        {
          name: block.fileName || (image ? "Image" : "Document"),
          key: block.fileKey || "",
          url: block.fileUrl || "",
          description: image ? "" : block.description || "",
        },
      ];
    }
    return [];
  }, [
    block.content,
    block.fileName,
    block.fileUrl,
    block.fileKey,
    block.description,
    image,
  ]);

  const resolveUrl = (entry) => {
    const raw = String(entry.url || "").trim();
    const key = String(entry.key || "").trim();
    let target = raw;
    if (
      (!target || target.startsWith("data:")) &&
      key &&
      !key.startsWith("data:")
    ) {
      target = `/uploads/${key.replace(/^\/?uploads\//, "")}`;
    }
    if (!target) return "";
    if (/^(https?:|data:|blob:)/i.test(target)) return target;
    const origin = new URL(API, window.location.origin).origin;
    return `${origin}/${target.replace(/^\/+/, "")}`;
  };

  const setFiles = (next) => {
    const first = next[0];
    updateBlock(index, {
      // Each entry keeps { name, key, url, mimeType, description }.
      content: JSON.stringify(next),
      fileName: first?.name || "",
      fileKey: first?.key || "",
      fileUrl: first?.url || "",
      // The old single description belongs to no card now; data is in content.
      ...(image ? {} : { description: "" }),
    });
  };

  const chooseFile = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length || uploading) return;
    setUploading(true);
    setUploadError("");
    const added = [];
    try {
      for (const file of selected) {
        const data = new FormData();
        data.append("file", file);
        const response = await axios.post(`${API}/api/notes/upload`, data, {
          headers: authHeaders(),
        });
        const uploaded = response.data?.file;
        if (!uploaded?.url)
          throw new Error(`Upload URL missing for ${file.name}`);
        added.push({
          name: uploaded.originalName || file.name,
          key: uploaded.key || uploaded.fileName || "",
          url: uploaded.url,
          mimeType: uploaded.mimeType || file.type || "",
          description: "", // New documents start with independent descriptions.
        });
      }
      setFiles([...files, ...added]);
    } catch (error) {
      console.error("NOTE FILE UPLOAD ERROR:", error);
      if (added.length) setFiles([...files, ...added]);
      setUploadError(
        error.response?.data?.message || error.message || "Upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fileIndex) => {
    if (!window.confirm("Remove this file from the note?")) return;
    setFiles(files.filter((_, i) => i !== fileIndex));
  };

  const filePicker = (
    <input
      ref={inputRef}
      type="file"
      accept={
        accept ||
        (image
          ? "image/*"
          : ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,image/*")
      }
      multiple
      onChange={chooseFile}
      disabled={uploading}
      aria-label={image ? "Choose images" : "Choose documents"}
      className="note-document-hidden-input"
      style={{ display: "none" }}
    />
  );

  const attachmentList = (
    <div className="note-file-list">
      <div className="note-file-list-title">
        Uploaded {image ? "images" : "documents"} ({files.length})
      </div>
      {files.length === 0 && (
        <div className="note-file-empty">
          {image ? "No images uploaded yet." : "No documents uploaded yet."}
        </div>
      )}
      {files.map((file, fileIndex) => {
        const url = resolveUrl(file);
        const isImage =
          image ||
          /\.(png|jpe?g|gif|webp|bmp)$/i.test(file.name || "") ||
          file.mimeType?.startsWith("image/");
        return (
          <div
            className="note-attachment"
            key={`${file.key || file.url}-${fileIndex}`}
          >
            {isImage && url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="note-attachment-thumb"
              >
                <img src={url} alt={file.name} />
              </a>
            ) : (
              <span className="note-attachment-icon">📄</span>
            )}
            <div className="note-attachment-details">
              {url ? (
                <a
                  className="note-attachment-name"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={file.name}
                >
                  {file.name}
                </a>
              ) : (
                <strong>{file.name}</strong>
              )}
              <small>
                {url ? "Click filename to open" : "File URL unavailable"}
              </small>
            </div>
            {url && (
              <a
                className="note-attachment-open"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open
              </a>
            )}
            <button
              type="button"
              className="note-attachment-remove"
              title="Remove from note"
              onClick={() => removeFile(fileIndex)}
            >
              ×
            </button>
          </div>
        );
      })}
      {uploadError && (
        <div className="note-upload-error" role="alert">
          {uploadError}
        </div>
      )}
    </div>
  );

  if (!image) {
    return (
      <div className="note-document-layout">
        <div className="note-document-column-heading">Description</div>
        <div className="note-document-upload-heading">
          <button
            type="button"
            className="note-document-upload-top"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? "Uploading..." : "📎 Upload Document"}
          </button>
        </div>
        {filePicker}
        {files.length === 0 && (
          <>
            <div className="note-document-empty-description">
              Upload a document to add its own description.
            </div>
            <div className="note-file-empty">No documents uploaded yet.</div>
          </>
        )}
        {files.map((file, fileIndex) => {
          const url = resolveUrl(file);
          const isImage =
            /\.(png|jpe?g|gif|webp|bmp)$/i.test(file.name || "") ||
            file.mimeType?.startsWith("image/");
          const fileKey = `${file.key || file.url || file.name}-${fileIndex}`;
          const descriptionId = `document-desc-${cardKey(block)}-${fileIndex}`;
          return (
            <React.Fragment key={fileKey}>
              {/* LEFT: description belongs to this exact document. */}
              <div className="note-document-description-row">
                <label
                  htmlFor={descriptionId}
                  className="note-document-description-accessible-label"
                >
                  Description for {file.name}
                </label>
                <AutoGrowDocumentDescription
                  id={descriptionId}
                  value={file.description || ""}
                  onChange={(description) => {
                    const nextFiles = files.map((entry, entryIndex) =>
                      entryIndex === fileIndex
                        ? { ...entry, description }
                        : entry,
                    );
                    setFiles(nextFiles);
                  }}
                />
              </div>
              {/* RIGHT: matching document, kept aligned with its description. */}
              <div className="note-document-attachment-row">
                <div className="note-attachment">
                  {isImage && url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="note-attachment-thumb"
                    >
                      <img src={url} alt={file.name} />
                    </a>
                  ) : (
                    <span className="note-attachment-icon">📄</span>
                  )}
                  <div className="note-attachment-details">
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="note-attachment-name"
                        title={file.name}
                      >
                        {file.name}
                      </a>
                    ) : (
                      <strong>{file.name}</strong>
                    )}
                    <small>
                      {url ? "Click filename to open" : "File URL unavailable"}
                    </small>
                  </div>
                  {url && (
                    <a
                      className="note-attachment-open"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open
                    </a>
                  )}
                  <button
                    type="button"
                    className="note-attachment-remove"
                    aria-label={`Remove ${file.name}`}
                    title="Remove this document"
                    onClick={() => removeFile(fileIndex)}
                  >
                    ×
                  </button>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {uploadError && (
          <div className="note-upload-error note-document-error" role="alert">
            {uploadError}
          </div>
        )}
      </div>
    );
  }

  // The image component keeps its existing two-column upload/list layout.
  return (
    <div className="note-file-block note-file-split">
      <label className={`note-file-drop ${uploading ? "uploading" : ""}`}>
        <span>{uploading ? "⏳" : "🖼"}</span>
        <strong>{uploading ? "Uploading..." : "Choose Images"}</strong>
        <small>Select one or multiple images</small>
        {filePicker}
      </label>
      {attachmentList}
    </div>
  );
};

// This modal operates on ONE selected card, never the whole-note email route.
const CardShareModal = ({
  users,
  card,
  noteShares,
  ownerId,
  feedback,
  busy,
  shareUser,
  emailCard,
  close,
}) => {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("users");
  const [recipient, setRecipient] = useState("");
  const matches = users.filter((user) =>
    `${user.name || ""} ${user.email || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <div className="note-modal-overlay" onMouseDown={close}>
      <div
        className="share-note-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="note-modal-header">
          <div>
            <h2>Share this card</h2>
            <p>
              <strong>{card?.title?.trim() || cardLabel(card?.type)}</strong> —
              only this card; other cards are not included.
            </p>
          </div>
          <button type="button" onClick={close} disabled={busy}>
            ×
          </button>
        </div>
        <div className="share-modal-content">
          <div
            className="note-card-share-tabs"
            role="tablist"
            aria-label="Share selected card"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === "users"}
              className={tab === "users" ? "active" : ""}
              onClick={() => setTab("users")}
              disabled={busy}
            >
              ↗ Share with ERP user
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "email"}
              className={tab === "email" ? "active" : ""}
              onClick={() => setTab("email")}
              disabled={busy}
            >
              ✉ Email this card
            </button>
          </div>
          {tab === "users" ? (
            <>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search ERP user..."
                aria-label="Find ERP user for this card"
              />
              {feedback && (
                <p
                  className="note-share-feedback"
                  role="status"
                  aria-live="polite"
                >
                  {feedback}
                </p>
              )}
              <div className="share-user-list">
                {matches.map((user) => {
                  const owner = sameUserId(ownerId, user._id);
                  const directlyShared = isSharedWith(
                    card?.sharedWith,
                    user._id,
                  );
                  const sharedViaNote = isSharedWith(noteShares, user._id);
                  return (
                    <div
                      className={`share-user${directlyShared || sharedViaNote ? " share-user--shared" : ""}`}
                      key={user._id}
                    >
                      <div className="user-avatar">{user.name?.[0] || "U"}</div>
                      <div>
                        <strong>{user.name}</strong>
                        <span>{user.email}</span>
                        {sharedViaNote && !directlyShared && (
                          <small className="note-share-via-note">
                            Has full-note access
                          </small>
                        )}
                      </div>
                      {owner ? (
                        <span className="note-share-status note-share-status--owner">
                          Owner
                        </span>
                      ) : directlyShared ? (
                        <span
                          className="note-share-status"
                          aria-label="Card already shared"
                        >
                          ✓ Shared
                        </span>
                      ) : (
                        <div className="note-share-row-actions">
                          {sharedViaNote && (
                            <span className="note-share-status">
                              ✓ Via note
                            </span>
                          )}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => shareUser(user._id)}
                          >
                            {busy ? "Saving..." : "Share this card"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {matches.length === 0 && <p>No matching ERP users.</p>}
              </div>
              <small className="note-card-share-hint">
                ERP sharing grants view access to this card only.
              </small>
            </>
          ) : (
            <form
              className="note-card-email-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!busy && recipient.trim()) emailCard(recipient);
              }}
            >
              <label htmlFor="note-selected-card-email">Recipient email</label>
              <input
                id="note-selected-card-email"
                type="email"
                placeholder="name@company.com"
                required
                value={recipient}
                onChange={(event) => setRecipient(event.target.value)}
                disabled={busy}
              />
              <p>
                Only this card's title, content and available attachments will
                be emailed. No other cards.
              </p>
              <button type="submit" disabled={busy || !recipient.trim()}>
                {busy ? "Sending..." : "✉ Send this card"}
              </button>
              <small className="note-card-share-hint">
                Your backend must have the card-email route and SMTP configured
                for actual sending.
              </small>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const ShareModal = ({
  users,
  email,
  setEmail,
  shareUser,
  sendEmail,
  sharedWith,
  ownerId,
  busy,
  feedback,
  close,
}) => {
  const [search, setSearch] = useState("");
  const [permissionChoices, setPermissionChoices] = useState({});

  const filteredUsers = users.filter((user) => {
    const key = search.toLowerCase();

    return (
      user.name?.toLowerCase().includes(key) ||
      user.email?.toLowerCase().includes(key)
    );
  });

  return (
    <div className="note-modal-overlay" onMouseDown={close}>
      <div
        className="share-note-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="note-modal-header">
          <div>
            <h2>Share Complete Note</h2>

            <p>
              This shares every card. To share one card only, use that card's
              Share card button.
            </p>
          </div>

          <button onClick={close}>×</button>
        </div>

        <div className="share-modal-content">
          <input
            placeholder="Search ERP user..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {feedback && (
            <p className="note-share-feedback" role="status" aria-live="polite">
              {feedback}
            </p>
          )}
          <div className="share-user-list">
            {filteredUsers.map((user) => {
              const owner = sameUserId(ownerId, user._id);
              const existingShare = (sharedWith || []).find((entry) =>
                sameUserId(entry.user, user._id),
              );
              const alreadyShared = Boolean(existingShare);
              const currentPermission =
                existingShare?.permission ||
                permissionChoices[user._id] ||
                "edit";
              return (
                <div
                  className={`share-user${alreadyShared ? " share-user--shared" : ""}`}
                  key={user._id}
                >
                  <div className="user-avatar">{user.name?.[0] || "U"}</div>
                  <div>
                    <strong>{user.name}</strong>
                    <span>{user.email}</span>
                  </div>
                  {owner ? (
                    <span className="note-share-status note-share-status--owner">
                      Owner
                    </span>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginLeft: "auto",
                      }}
                    >
                      {alreadyShared && (
                        <span
                          className="note-share-status"
                          aria-label="Note already shared"
                        >
                          ✓ Shared
                        </span>
                      )}
                      <select
                        aria-label={`Permission for ${user.name}`}
                        value={currentPermission}
                        disabled={busy}
                        onChange={(event) => {
                          const nextPermission = event.target.value;
                          if (alreadyShared)
                            shareUser(user._id, nextPermission);
                          else
                            setPermissionChoices((previous) => ({
                              ...previous,
                              [user._id]: nextPermission,
                            }));
                        }}
                        style={{ minWidth: 82, padding: "7px 4px" }}
                      >
                        <option value="view">View</option>
                        <option value="edit">Edit</option>
                      </select>
                      {!alreadyShared && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => shareUser(user._id, currentPermission)}
                        >
                          {busy ? "Sharing..." : "Share"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {filteredUsers.length === 0 && <p>No matching ERP users.</p>}
          </div>

          <div className="share-email-section">
            <strong>Send by Email</strong>

            <div>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email address"
              />

              <button onClick={sendEmail}>Send</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteEditorPage;
